// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac Dashboard
// Viktor performance analytics and monitoring
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import { UseQueryResult } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { QueryStatus } from '@/components/QueryStatus';
import { SkeletonStats, SkeletonChart, SkeletonCard } from '@/components/ui/skeleton-card';
import { useAIAgentStats, type AIAgentStats } from '@/hooks/useAIAgentStats';
import { AgentHeader } from '@/components/ai-prodavac/AgentHeader';
import { StatCards } from '@/components/ai-prodavac/StatCards';
import { ConversationFunnel } from '@/components/ai-prodavac/ConversationFunnel';
import { LeadQualityChart } from '@/components/ai-prodavac/LeadQualityChart';
import { VersionComparison } from '@/components/ai-prodavac/VersionComparison';
import { TrendsChart } from '@/components/ai-prodavac/TrendsChart';
import { Phase3ReadinessCard } from '@/components/ai-prodavac/Phase3ReadinessCard';
import { HealthScoreCard } from '@/components/ai-prodavac/HealthScoreCard';
import { MorningBrief } from '@/components/ai-prodavac/MorningBrief';
import { GhostRiskMonitor } from '@/components/ai-prodavac/GhostRiskMonitor';
import { ForecastChart } from '@/components/ai-prodavac/ForecastChart';
import { WinLossInsights } from '@/components/ai-prodavac/WinLossInsights';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RefreshCw, Bot, ExternalLink } from 'lucide-react';
import {
  useForecast,
  useGhostRisk,
  useHealthScore,
  useMorningBrief,
  useWinLoss,
} from '@/hooks/useRevenueIntelligence';
import { useLinkedInAnalytics } from '@/hooks/useLinkedInAnalytics';
import { LinkedInDraftFunnel } from '@/components/ai-prodavac/LinkedInDraftFunnel';
import { MethodologyPerformance } from '@/components/ai-prodavac/MethodologyPerformance';
import { HypothesisVsReality } from '@/components/ai-prodavac/HypothesisVsReality';
import { ResponseRateTrends } from '@/components/ai-prodavac/ResponseRateTrends';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function AIProdavacPage() {
  const { versionId } = useParams<{ versionId?: string }>();
  const navigate = useNavigate();
  const dateRange = useMemo(() => ({
    from: startOfDay(subDays(new Date(), 29)),
    to: endOfDay(new Date()),
  }), []);

  // Fetch data
  const {
    data: stats,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useAIAgentStats(versionId, dateRange);
  const healthScoreQuery = useHealthScore(7);
  const morningBriefQuery = useMorningBrief();
  const ghostRiskQuery = useGhostRisk(6);
  const forecastQuery = useForecast(30);
  const winLossQuery = useWinLoss();
  const linkedInAnalyticsQuery = useLinkedInAnalytics(30);

  // Handle version change
  const handleVersionChange = (newVersionId: string): void => {
    if (newVersionId === stats?.currentVersion.id) {
      // Navigate to default route (current active version)
      navigate('/ai-prodavac');
    } else {
      // Navigate to specific version
      navigate(`/ai-prodavac/${newVersionId}`);
    }
  };

  useEffect(() => {
    document.title = 'AI Sales Agent — Viktor | Tacit CRM';
  }, []);

  return (
    <Layout>
      <ErrorBoundary>
        <main className="space-y-6 px-4 py-6 sm:px-6">
          <header className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-elevation-1 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                  AI Sales Agent — Viktor
                </h1>
                {stats?.currentVersion.isActive && (
                  <span className="rounded-full border border-status-success/30 bg-status-success/10 px-2 py-0.5 text-xs font-medium text-status-success">
                    Active
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Agent status, guidance, and performance analytics.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="min-h-11 shrink-0 self-start sm:self-auto md:min-h-11"
            >
              <RefreshCw
                className={isFetching ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'}
                aria-hidden="true"
              />
              {isFetching ? 'Refreshing agent stats...' : 'Refresh agent stats'}
            </Button>
          </header>

          {/* Main Stats Section - With graceful error handling */}
          <QueryStatus
            query={{
              data: stats,
              isLoading,
              isFetching: isLoading,
              isError: !!error,
              error: error || null,
              refetch,
            } as UseQueryResult<AIAgentStats, Error>}
            loading={
              <div className="space-y-6">
                <SkeletonCard className="h-32" />
                <SkeletonStats />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <SkeletonChart />
                  <SkeletonChart />
                </div>
              </div>
            }
            error={(err, retry) => {
              // Special handling for "no chatbot" error
              if (err.message?.includes('Version not found') || err.message?.includes('No active chatbot')) {
                return (
                  <Card className="p-12">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                        <Bot className="w-10 h-10 text-primary" />
                      </div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Viktor is not active yet
                      </h2>
                      <p className="text-muted-foreground max-w-md mb-6">
                        Your organization does not have an active AI Sales Agent version yet.
                        Create the first version in DeliveryOS to get started.
                      </p>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Button asChild className="min-h-11">
                          <a href="https://os.qualiteh.com" target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open DeliveryOS
                          </a>
                        </Button>
                        <Button
                          variant="outline"
                          className="min-h-11"
                          onClick={retry}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Check again
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              }
              // Default error UI
              return null;
            }}
          >
            {(data: AIAgentStats) => (
              <>
                {/* Header with Viktor info and version selector */}
                <AgentHeader
                  version={data.currentVersion}
                  systemPrompt={data.currentVersion.systemPrompt}
                  allVersions={data.allVersions}
                  onVersionChange={handleVersionChange}
                />

                {/* Time range selector */}
                <div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Performance Overview</h2>
                    <p className="text-sm text-muted-foreground">
                      {data.kpis.totalSessions} sessions • {data.kpis.leadsCapured} leads
                    </p>
                  </div>
                </div>

                {/* KPI Cards */}
                <StatCards kpis={data.kpis} trends={data.trends} />

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Conversation Funnel */}
                  <ConversationFunnel funnel={data.funnel} />

                  {/* Lead Quality */}
                  <LeadQualityChart data={data.leadQuality} />

                  {/* Daily Trends */}
                  <div className="lg:col-span-2">
                    <TrendsChart data={data.trends} />
                  </div>
                </div>

                {/* Version Comparison & Phase 3 */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Version History */}
                  <div className="lg:col-span-2">
                    <VersionComparison
                      versions={data.versions}
                      currentVersionId={data.currentVersion.id}
                      onVersionSelect={handleVersionChange}
                    />
                  </div>

                  {/* Phase 3 Readiness */}
                  <Phase3ReadinessCard phase3={data.phase3} />
                </div>
              </>
            )}
          </QueryStatus>

          <section className="space-y-4" aria-labelledby="executive-overview-heading">
            <h2
              id="executive-overview-heading"
              className="text-lg font-semibold text-foreground"
            >
              Executive Overview
            </h2>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <QueryStatus
                query={healthScoreQuery}
                loading={<SkeletonCard />}
              >
                {(data) => <HealthScoreCard data={data} />}
              </QueryStatus>

              <div className="lg:col-span-2">
                <QueryStatus
                  query={morningBriefQuery}
                  loading={<SkeletonCard className="h-full" />}
                >
                  {(data) => <MorningBrief data={data} />}
                </QueryStatus>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <QueryStatus
                query={ghostRiskQuery}
                loading={<SkeletonCard />}
              >
                {(data) => <GhostRiskMonitor items={data.items} />}
              </QueryStatus>

              <div className="lg:col-span-2">
                <QueryStatus
                  query={forecastQuery}
                  loading={<SkeletonChart />}
                >
                  {(data) => <ForecastChart rows={data.rows} />}
                </QueryStatus>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <QueryStatus
                query={winLossQuery}
                loading={<SkeletonCard />}
              >
                {(data) => <WinLossInsights rows={data.rows} />}
              </QueryStatus>
            </div>
          </section>

          <section
            className="space-y-4"
            aria-labelledby="linkedin-outreach-performance-heading"
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <h2
                id="linkedin-outreach-performance-heading"
                className="text-lg font-semibold"
              >
                LinkedIn Outreach Performance
              </h2>
              <span className="text-sm text-muted-foreground">Last 30 days</span>
            </div>

            <QueryStatus
              query={linkedInAnalyticsQuery}
              loading={
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <SkeletonCard className="h-96" />
                  <SkeletonCard className="h-96" />
                </div>
              }
            >
              {(linkedInStats) => (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <LinkedInDraftFunnel data={linkedInStats.funnel} />
                    <MethodologyPerformance data={linkedInStats.methodologyComparison} />
                  </div>

                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <HypothesisVsReality data={linkedInStats.calibration} />
                    <ResponseRateTrends data={linkedInStats.trends} />
                  </div>
                </div>
              )}
            </QueryStatus>
          </section>
        </main>
      </ErrorBoundary>
    </Layout>
  );
}
